<template>
  <AnimationComponent>
    <section class="py-16 md:py-24">
      <div class="max-w-7xl mx-auto px-8">
        <!-- Desktop: 2-column layout -->
        <div class="flex flex-col lg:flex-row gap-12 lg:gap-24">
          <!-- Left Column: Heading and Description -->
          <div class="flex flex-col gap-5 flex-1">
            <h2
              class="text-3xl md:text-4xl font-semibold leading-[38px] md:leading-11 tracking-tight text-grey-dark-50"
            >
              {{ title }}
            </h2>
            <p class="text-base md:text-xl font-normal leading-6 md:leading-[30px] text-grey-dark-400">
              {{ description }}
            </p>
          </div>

          <!-- Right Column: Features List -->
          <div class="flex flex-col gap-12 flex-1">
            <!-- Icon + Text Features -->
            <template v-if="hasIconFeatures">
              <div v-for="(feature, index) in features" :key="index" class="flex gap-4 items-start">
                <!-- Icon -->
                <div
                  class="border border-grey-dark-300 rounded-[10px] custom-shadow w-12 h-12 flex items-center justify-center shrink-0"
                >
                  <component :is="getIconComponent(feature.icon!)" class="w-6 h-6 text-grey-dark-50" />
                </div>

                <!-- Content -->
                <div class="flex flex-col gap-2 flex-1 pt-2.5">
                  <h3
                    class="text-lg md:text-xl font-medium md:font-semibold leading-7 md:leading-[30px] text-grey-dark-50"
                  >
                    {{ feature.title }}
                  </h3>
                  <p class="text-base font-normal leading-6 text-grey-dark-300">
                    {{ feature.description }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Image Solutions Grid -->
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div v-for="(feature, index) in features" :key="index" class="flex items-center justify-center">
                <img v-if="feature.image" :src="feature.image" :alt="feature.alt || ''" class="max-w-full h-auto" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </AnimationComponent>
</template>

<script setup lang="ts">
import { getIconComponent } from '@/utils/icons';
import { computed } from 'vue';
import AnimationComponent from '../AnimationComponent.vue';

interface Feature {
  icon?: string;
  title?: string;
  description?: string;
  image?: string;
  alt?: string;
}

interface Props {
  title: string;
  description?: string;
  features: Feature[];
}

const props = defineProps<Props>();

// Determine if we're rendering icon features or image features
const hasIconFeatures = computed(() => {
  return props.features.length > 0 && props.features[0]?.icon != null && props.features[0]?.icon !== '';
});
</script>

<style scoped>
.custom-shadow {
  @apply shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)];
}
</style>
